// Iter 62cq — Vue Clients Analytics : Vues + Argent par client, 6 mois d'historique.
// Founder-only. Rendue dans un onglet "Analytics" du Hub Clients.
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import {
  Eye, TrendingUp, BarChart3, Plus, X, Trash2, Video, Pencil,
} from '../components/icons';

const SORTS = [
  { id: 'revenue', label: 'Revenu' },
  { id: 'views', label: 'Vues' },
  { id: 'growth', label: 'Croissance' },
];

const MONTH_LABEL_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

function fmtMonthShort(iso /* "2026-08" */) {
  if (!iso || iso.length < 7) return '';
  const m = parseInt(iso.slice(5, 7), 10);
  const y = iso.slice(2, 4);
  return `${MONTH_LABEL_FR[m - 1]} ${y}`;
}

function fmtNumber(n) {
  n = Number(n || 0);
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace('.0', '') + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1).replace('.0', '') + 'k';
  return String(Math.round(n));
}

function fmtEuro(n) {
  n = Number(n || 0);
  if (n === 0) return '0 €';
  if (n >= 1000) {
    return n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
  }
  return `${Math.round(n)} €`;
}

export default function ClientsAnalyticsView() {
  const [data, setData] = useState(null); // { months, clients }
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('revenue');
  const [openClient, setOpenClient] = useState(null); // full client entry from analytics

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/clients/analytics?months=6');
      setData(r.data);
    } catch (e) {
      toast.error('Impossible de charger les analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sorted = useMemo(() => {
    if (!data) return [];
    const arr = [...(data.clients || [])];
    if (sortBy === 'revenue') arr.sort((a, b) => (b.total_revenue || 0) - (a.total_revenue || 0));
    else if (sortBy === 'views') arr.sort((a, b) => (b.total_views || 0) - (a.total_views || 0));
    else if (sortBy === 'growth') arr.sort((a, b) => (b.views_growth_pct || 0) - (a.views_growth_pct || 0));
    return arr;
  }, [data, sortBy]);

  // Totals row
  const totals = useMemo(() => {
    if (!data) return { views: 0, revenue: 0, delivered: 0 };
    return (data.clients || []).reduce((acc, c) => ({
      views: acc.views + (c.total_views || 0),
      revenue: acc.revenue + (c.total_revenue || 0),
      delivered: acc.delivered + (c.delivered_count || 0),
    }), { views: 0, revenue: 0, delivered: 0 });
  }, [data]);

  return (
    <div data-testid="clients-analytics" style={{ padding: '0 22px 100px' }}>
      {/* Totals bar */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(160px, 1fr))',
        gap: 12,
        marginBottom: 18,
      }}>
        <TotalCard
          testId="analytics-total-revenue"
          icon={<BarChart3 size={14} color="#F5C56A" />}
          label="Revenu total (6 mois)"
          value={fmtEuro(totals.revenue)}
          accent="#F5C56A"
        />
        <TotalCard
          testId="analytics-total-views"
          icon={<Eye size={14} color="#5BBEFC" />}
          label="Vues cumulées (6 mois)"
          value={fmtNumber(totals.views)}
          accent="#5BBEFC"
        />
        <TotalCard
          testId="analytics-total-delivered"
          icon={<Video size={14} color="#34D399" />}
          label="Vidéos livrées"
          value={fmtNumber(totals.delivered)}
          accent="#34D399"
        />
      </div>

      {/* Sort selector */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14,
        flexWrap: 'wrap',
      }}>
        <span style={{
          fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
          color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.14em',
        }}>Trier par</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {SORTS.map((s) => {
            const active = sortBy === s.id;
            return (
              <button
                key={s.id}
                data-testid={`analytics-sort-${s.id}`}
                onClick={() => setSortBy(s.id)}
                style={{
                  padding: '7px 14px', borderRadius: 999,
                  border: `1px solid ${active ? '#F5C56A' : 'var(--bd)'}`,
                  background: active ? 'linear-gradient(140deg, rgba(245,197,106,0.18), rgba(245,197,106,0.06))' : 'var(--sf)',
                  color: active ? '#F5C56A' : 'var(--t2)',
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
                  cursor: 'pointer',
                  transition: 'all 0.15s var(--ease-out)',
                }}
              >{s.label}</button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="shimmer" style={{ height: 260, borderRadius: 16 }} />
      ) : sorted.length === 0 ? (
        <EmptyState />
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 14,
        }}>
          {sorted.map((c) => (
            <AnalyticsCard
              key={c.id}
              client={c}
              months={data?.months || []}
              onOpen={() => setOpenClient(c)}
            />
          ))}
        </div>
      )}

      {openClient && (
        <ClientAnalyticsDrawer
          client={openClient}
          months={data?.months || []}
          onClose={() => setOpenClient(null)}
          onSaved={() => { load(); }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function TotalCard({ testId, icon, label, value, accent }) {
  return (
    <div
      data-testid={testId}
      style={{
        borderRadius: 14,
        border: '1px solid var(--bd)',
        background: `linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01)), var(--sf)`,
        padding: '14px 16px',
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.06)`,
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5,
        color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.14em',
      }}>
        {icon}
        <span style={{ color: accent }}>{label}</span>
      </div>
      <div style={{
        fontFamily: 'Sora', fontWeight: 800, fontSize: 26,
        color: 'var(--t1)', marginTop: 6, letterSpacing: '-0.02em',
      }}>{value}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{
      padding: '60px 20px', textAlign: 'center',
      color: 'var(--t3)', fontFamily: 'Sora', fontSize: 13,
    }}>
      <BarChart3 size={30} color="var(--t4)" strokeWidth={1.4} />
      <div style={{ marginTop: 12, fontWeight: 700, color: 'var(--t2)' }}>Aucun client encore</div>
      <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--t4)' }}>
        Crée des clients dans l&apos;onglet Kanban pour voir leurs analytics
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function AnalyticsCard({ client, months, onOpen }) {
  const color = client.color || '#5BBEFC';
  const initials = (client.name || '?').split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('');
  const positive = (client.views_growth_pct || 0) >= 0;

  // Compute max value on both dims for sparkline y-scale
  const monthly = client.monthly || [];
  const maxViews = Math.max(1, ...monthly.map((m) => m.views || 0));
  const maxRevenue = Math.max(1, ...monthly.map((m) => m.revenue || 0));

  return (
    <button
      data-testid={`analytics-card-${client.id}`}
      onClick={onOpen}
      style={{
        display: 'flex', flexDirection: 'column',
        textAlign: 'left', gap: 14,
        borderRadius: 16,
        border: '1px solid var(--bd)',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.005)), var(--sf)',
        padding: 16,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 4px 14px rgba(0,0,0,0.24)',
        cursor: 'pointer',
        transition: 'all 0.18s var(--ease-out)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = color;
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--bd)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span
          aria-hidden
          style={{
            width: 42, height: 42, borderRadius: 12,
            display: 'grid', placeItems: 'center',
            background: `linear-gradient(140deg, ${color} 0%, rgba(255,255,255,0.15) 50%, ${color} 100%)`,
            color: '#0B1220', fontFamily: 'Sora', fontWeight: 800, fontSize: 15,
            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -6px 10px rgba(0,0,0,0.25), 0 4px 12px ${color}55`,
            flexShrink: 0,
          }}
        >{initials || '?'}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: 'Sora', fontWeight: 800, fontSize: 16,
            color: 'var(--t1)', letterSpacing: '-0.01em',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{client.name}</div>
          <div style={{
            fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5,
            color: 'var(--t3)', marginTop: 2,
            textTransform: 'uppercase', letterSpacing: '0.14em',
          }}>{client.delivered_count} vidéo{client.delivered_count > 1 ? 's' : ''} livrée{client.delivered_count > 1 ? 's' : ''}</div>
        </div>
        <div
          data-testid={`analytics-card-${client.id}-growth`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            padding: '4px 8px', borderRadius: 999,
            border: `1px solid ${positive ? 'rgba(52,211,153,0.35)' : 'rgba(255,107,107,0.35)'}`,
            background: positive ? 'rgba(52,211,153,0.10)' : 'rgba(255,107,107,0.10)',
            color: positive ? '#34D399' : '#FF6B6B',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5,
          }}
        >
          <TrendingUp size={10} style={{ transform: positive ? 'none' : 'scaleY(-1)' }} />
          {positive ? '+' : ''}{Math.round(client.views_growth_pct || 0)}%
        </div>
      </div>

      {/* KPIs row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <MetricPill
          testId={`analytics-card-${client.id}-revenue`}
          icon={<BarChart3 size={11} />}
          label="Revenu 6 mois"
          value={fmtEuro(client.total_revenue)}
          accent="#F5C56A"
        />
        <MetricPill
          testId={`analytics-card-${client.id}-views`}
          icon={<Eye size={11} />}
          label="Vues cumulées"
          value={fmtNumber(client.total_views)}
          accent="#5BBEFC"
        />
      </div>

      {/* Sparklines */}
      <Sparkline
        months={monthly}
        maxViews={maxViews}
        maxRevenue={maxRevenue}
        color={color}
      />
    </button>
  );
}

function MetricPill({ testId, icon, label, value, accent }) {
  return (
    <div
      data-testid={testId}
      style={{
        borderRadius: 10, padding: '9px 11px',
        border: '1px solid var(--bd)',
        background: 'var(--sf2)',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 5,
        fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5,
        color: accent, textTransform: 'uppercase', letterSpacing: '0.12em',
      }}>
        {icon}
        {label}
      </div>
      <div style={{
        fontFamily: 'Sora', fontWeight: 800, fontSize: 18,
        color: 'var(--t1)', marginTop: 3, letterSpacing: '-0.01em',
      }}>{value}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sparkline showing 6 months of views (bars) + revenue (dots+line)
function Sparkline({ months, maxViews, maxRevenue, color }) {
  const W = 260;
  const H = 60;
  const PAD_X = 6;
  const n = months.length || 1;
  const barW = (W - PAD_X * 2) / n * 0.6;
  const step = (W - PAD_X * 2) / n;

  // Points for revenue line
  const revPoints = months.map((m, i) => {
    const x = PAD_X + step * (i + 0.5);
    const y = H - 6 - ((m.revenue || 0) / maxRevenue) * (H - 12);
    return { x, y, val: m.revenue || 0 };
  });

  const linePath = revPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');

  return (
    <div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block', height: 60 }}>
        {/* baseline */}
        <line x1={0} x2={W} y1={H - 6} y2={H - 6} stroke="var(--bd)" strokeWidth="0.5" />
        {/* views bars */}
        {months.map((m, i) => {
          const x = PAD_X + step * i + (step - barW) / 2;
          const hh = ((m.views || 0) / maxViews) * (H - 12);
          const y = H - 6 - hh;
          return (
            <rect
              key={`b${i}`}
              x={x.toFixed(1)}
              y={y.toFixed(1)}
              width={barW.toFixed(1)}
              height={Math.max(1, hh).toFixed(1)}
              rx="1.5"
              fill="#5BBEFC"
              opacity={m.views > 0 ? 0.85 : 0.18}
            />
          );
        })}
        {/* revenue line */}
        {maxRevenue > 0 && (
          <>
            <path d={linePath} fill="none" stroke="#F5C56A" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            {revPoints.map((p, i) => (
              <circle key={`c${i}`} cx={p.x.toFixed(1)} cy={p.y.toFixed(1)} r="2" fill="#F5C56A" />
            ))}
          </>
        )}
      </svg>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        marginTop: 4, padding: '0 6px',
      }}>
        {months.map((m) => (
          <div
            key={m.month}
            style={{
              fontFamily: 'Sora', fontWeight: 700, fontSize: 9,
              color: 'var(--t4)', letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >{fmtMonthShort(m.month)}</div>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function ClientAnalyticsDrawer({ client, months, onClose, onSaved }) {
  const color = client.color || '#5BBEFC';
  const [revenues, setRevenues] = useState({}); // { month: amount }
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // month being saved

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [r1, r2] = await Promise.all([
          api.get(`/clients/${client.id}/revenues`),
          api.get(`/clients/${client.id}/delivered-videos`),
        ]);
        if (cancelled) return;
        const map = {};
        (r1.data || []).forEach((e) => { map[e.month] = e.amount; });
        setRevenues(map);
        setVideos(r2.data || []);
      } catch (e) {
        toast.error('Erreur chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [client.id]);

  const saveRevenue = async (month, rawValue) => {
    const amount = parseFloat(String(rawValue).replace(',', '.').replace(/[^\d.]/g, '') || '0');
    if (isNaN(amount) || amount < 0) {
      toast.error('Montant invalide');
      return;
    }
    setSaving(month);
    try {
      await api.post(`/clients/${client.id}/revenue`, { month, amount });
      setRevenues((prev) => ({ ...prev, [month]: amount }));
      toast.success('Revenu enregistré');
      onSaved && onSaved();
    } catch (e) {
      toast.error('Erreur sauvegarde');
    } finally {
      setSaving(null);
    }
  };

  const saveVideoViews = async (taskId, rawValue) => {
    const views = parseInt(String(rawValue).replace(/[^\d]/g, '') || '0', 10);
    if (isNaN(views) || views < 0) {
      toast.error('Nombre invalide');
      return;
    }
    try {
      await api.patch(`/tasks/${taskId}`, { video_views: views });
      setVideos((prev) => prev.map((v) => v.id === taskId ? { ...v, video_views: views } : v));
      toast.success('Vues mises à jour');
      onSaved && onSaved();
    } catch (e) {
      toast.error('Erreur sauvegarde');
    }
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 900,
          background: 'rgba(3,7,18,0.55)', backdropFilter: 'blur(6px)',
        }}
      />
      <aside
        data-testid="analytics-drawer"
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 901,
          width: 'min(500px, 100vw)',
          background: 'var(--bg)',
          borderLeft: '1px solid var(--bd)',
          boxShadow: '-24px 0 60px rgba(0,0,0,0.45)',
          display: 'flex', flexDirection: 'column',
          animation: 'slideInRight 0.22s var(--ease-out)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 20px 16px',
          borderBottom: '1px solid var(--bd)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span
            aria-hidden
            style={{
              width: 44, height: 44, borderRadius: 12,
              display: 'grid', placeItems: 'center',
              background: `linear-gradient(140deg, ${color} 0%, rgba(255,255,255,0.15) 50%, ${color} 100%)`,
              color: '#0B1220', fontFamily: 'Sora', fontWeight: 800, fontSize: 15,
              boxShadow: `inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -6px 10px rgba(0,0,0,0.25), 0 6px 14px ${color}55`,
              flexShrink: 0,
            }}
          >{(client.name || '?').split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('')}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: 20,
              color: 'var(--t1)', letterSpacing: '-0.01em',
            }}>{client.name}</div>
            <div style={{
              fontFamily: 'Sora', fontWeight: 600, fontSize: 11,
              color: 'var(--t3)', marginTop: 3,
              textTransform: 'uppercase', letterSpacing: '0.12em',
            }}>{fmtEuro(client.total_revenue)} · {fmtNumber(client.total_views)} vues · {client.delivered_count} vidéos</div>
          </div>
          <button
            data-testid="analytics-drawer-close"
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 8,
              display: 'grid', placeItems: 'center',
              background: 'var(--sf)', border: '1px solid var(--bd)',
              color: 'var(--t2)', cursor: 'pointer',
            }}
            aria-label="Fermer"
          ><X size={15} /></button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {/* Revenue section */}
          <SectionTitle icon={<BarChart3 size={12} color="#F5C56A" />} label="Revenu par mois" accent="#F5C56A" />
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8,
            marginTop: 8, marginBottom: 22,
          }}>
            {months.map((m) => (
              <RevenueInput
                key={m}
                month={m}
                initialAmount={revenues[m] || 0}
                onSave={(val) => saveRevenue(m, val)}
                saving={saving === m}
              />
            ))}
          </div>

          {/* Delivered videos */}
          <SectionTitle icon={<Video size={12} color="#5BBEFC" />} label={`Vidéos livrées · ${videos.length}`} accent="#5BBEFC" />
          {loading ? (
            <div className="shimmer" style={{ height: 80, borderRadius: 10, marginTop: 8 }} />
          ) : videos.length === 0 ? (
            <div style={{
              padding: '24px 12px', textAlign: 'center',
              color: 'var(--t4)', fontFamily: 'Sora', fontSize: 12,
              border: '1px dashed var(--bd)', borderRadius: 10,
              marginTop: 8,
            }}>
              Aucune vidéo livrée pour ce client (les tâches vidéo passées à &quot;Terminé&quot; apparaissent ici)
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
              {videos.map((v) => (
                <VideoRow
                  key={v.id}
                  video={v}
                  onSave={(val) => saveVideoViews(v.id, val)}
                />
              ))}
            </div>
          )}
        </div>
      </aside>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(24px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
}

function SectionTitle({ icon, label, accent }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      fontFamily: 'Sora', fontWeight: 800, fontSize: 11,
      color: accent, textTransform: 'uppercase', letterSpacing: '0.16em',
    }}>
      {icon}
      {label}
    </div>
  );
}

function RevenueInput({ month, initialAmount, onSave, saving }) {
  const [value, setValue] = useState(initialAmount ? String(initialAmount) : '');
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    setValue(initialAmount ? String(initialAmount) : '');
  }, [initialAmount]);

  const commit = () => {
    const raw = value.trim();
    const parsed = parseFloat(raw.replace(',', '.').replace(/[^\d.]/g, '') || '0');
    if (parsed === Number(initialAmount)) return;
    onSave(raw || '0');
  };

  return (
    <label
      style={{
        display: 'flex', flexDirection: 'column', gap: 4,
        padding: '8px 10px',
        borderRadius: 10,
        border: `1px solid ${focused ? '#F5C56A' : 'var(--bd)'}`,
        background: 'var(--sf2)',
        transition: 'border-color 0.15s',
      }}
    >
      <span style={{
        fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5,
        color: 'var(--t4)', textTransform: 'uppercase', letterSpacing: '0.14em',
      }}>{fmtMonthShort(month)}</span>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <input
          data-testid={`analytics-revenue-input-${month}`}
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); commit(); }}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          placeholder="0"
          disabled={saving}
          style={{
            flex: 1, minWidth: 0,
            background: 'transparent',
            border: 'none', outline: 'none',
            color: 'var(--t1)',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 16,
            padding: 0,
          }}
        />
        <span style={{
          fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
          color: 'var(--t3)',
        }}>€</span>
      </div>
    </label>
  );
}

function VideoRow({ video, onSave }) {
  const [value, setValue] = useState(String(video.video_views || 0));
  const [editing, setEditing] = useState(false);
  const initial = video.video_views || 0;

  useEffect(() => { setValue(String(video.video_views || 0)); }, [video.video_views]);

  const commit = () => {
    setEditing(false);
    const parsed = parseInt(value.replace(/[^\d]/g, '') || '0', 10);
    if (parsed === initial) return;
    onSave(value);
  };

  const deliveredMonth = (video.delivered_at || '').slice(0, 7);

  return (
    <div
      data-testid={`analytics-video-${video.id}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 12px',
        borderRadius: 10,
        border: '1px solid var(--bd)',
        background: 'var(--sf)',
      }}
    >
      <Video size={14} color="#5BBEFC" strokeWidth={1.8} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
          color: 'var(--t1)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{video.title}</div>
        <div style={{
          fontFamily: 'Sora', fontWeight: 600, fontSize: 10,
          color: 'var(--t4)', marginTop: 2,
          textTransform: 'uppercase', letterSpacing: '0.12em',
        }}>Livrée · {fmtMonthShort(deliveredMonth)}{video.video_format ? ` · ${video.video_format}` : ''}</div>
      </div>
      {editing ? (
        <input
          data-testid={`analytics-video-input-${video.id}`}
          autoFocus
          type="text"
          inputMode="numeric"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setValue(String(initial)); setEditing(false); } }}
          style={{
            width: 80,
            background: 'var(--sf2)',
            border: '1px solid #5BBEFC',
            borderRadius: 8,
            padding: '5px 8px',
            color: 'var(--t1)',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 13,
            outline: 'none',
            textAlign: 'right',
          }}
        />
      ) : (
        <button
          data-testid={`analytics-video-edit-${video.id}`}
          onClick={() => setEditing(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '5px 9px', borderRadius: 8,
            border: '1px solid rgba(91,190,252,0.35)',
            background: 'rgba(91,190,252,0.10)',
            color: '#5BBEFC',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
            cursor: 'pointer',
          }}
        >
          <Eye size={11} />
          {fmtNumber(initial)}
          <Pencil size={9} style={{ marginLeft: 2, opacity: 0.7 }} />
        </button>
      )}
    </div>
  );
}
